import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseView } from '@/components/base'
import { Modal } from '@/components/Modal'
import { Label } from '@/features/dashboard/components/DashboardHeader/components/Profile/components/Label'
import { useFetchMessagingMethods } from '@/domain/messagingMethods'
import type {
  MessagingMethodCode,
  MessagingMethodsDTO,
} from '@/domain/messagingMethods/messagingMethodsTypes'
import { AppIcons } from '@/components/AppIcons'
import { responsiveValueAdapter } from '@/utils/responsive'

interface MessagingMethodsBottomSheetProps {
  type?: 'recoverPassword' | 'updateEmail'
  onMethodSelected: (
    method: MessagingMethodCode,
    methodData?: MessagingMethodsDTO,
  ) => void
  isOpen: boolean
  onClose: () => void
  isLoading?: boolean
  selectedMessagingMethod: MessagingMethodCode | null
  setSelectedMessagingMethod: (method: MessagingMethodCode) => void
}

export const MessagingMethodsBottomSheet: React.FC<
  MessagingMethodsBottomSheetProps
> = ({
  type = 'recoverPassword',
  onMethodSelected,
  isOpen,
  onClose,
  isLoading = false,
  selectedMessagingMethod,
  setSelectedMessagingMethod,
}) => {
  const { t } = useTranslation()

  const { data: messagingMethodsData, isLoading: isLoadingMethods } =
    useFetchMessagingMethods({
      is_available: true,
    })

  const messagingMethods = messagingMethodsData?.methods || []
  const isMethodsLoading = isLoadingMethods || isLoading

  useEffect(() => {
    if (messagingMethods.length > 0 && !selectedMessagingMethod) {
      const availableMethod = messagingMethods.find(
        (method) => method.available,
      )
      if (availableMethod) {
        setSelectedMessagingMethod(availableMethod.code)
      }
    }
  }, [messagingMethods, selectedMessagingMethod])

  const handleMethodClick = (methodCode: MessagingMethodCode) => {
    setSelectedMessagingMethod(methodCode)
    onMethodSelected(methodCode)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} showHandle={true}>
      <BaseView
        style={{
          gap: responsiveValueAdapter(12, true),
          paddingBottom: responsiveValueAdapter(18, true),
          scrollbarWidth: 'none',
        }}
      >
        <div
          className="flex flex-col flex-1 px-3"
          style={{
            paddingTop: responsiveValueAdapter(14, true),
          }}
        >
          <div
            className={`flex flex-col gap-4 ${
              type === 'updateEmail' ? 'px-4' : ''
            }`}
          >
            <Label type="titleBottomSheet">
              {t('bottomSheets:chooseMessagingApp.title')}
            </Label>
            <Label type="descriptionBottomSheet">
              {t('bottomSheets:chooseMessagingApp.description')}
            </Label>
          </div>

          <BaseView
            gap={responsiveValueAdapter(12, true)}
            style={{ marginTop: responsiveValueAdapter(24, true) }}
          >
            <BaseView gap={responsiveValueAdapter(12, true)}>
              {messagingMethods.map((method) => (
                <MethodComponent
                  key={method.code}
                  method={method}
                  isLoading={isMethodsLoading}
                  onClick={() =>
                    method.available && handleMethodClick(method.code)
                  }
                />
              ))}
            </BaseView>
          </BaseView>

          {/* <div className="flex flex-row items-center justify-center gap-3 mt-8">
            <AppButton
              onClick={handleMethodSelect}
              type="purpleGradientWithContour"
              label={t('form:actions.confirm')}
              disabled={
                messagingMethods.length === 0 || !selectedMessagingMethod
              }
              isLoading={isMethodsLoading}
              style={{ width: '100%' }}
            />
          </div> */}
        </div>
      </BaseView>
    </Modal>
  )
}

const MethodComponent = ({
  method,
  onClick,
  isLoading,
}: {
  method: MessagingMethodsDTO
  onClick: () => void
  isLoading: boolean
}) => {
  return (
    <div
      className={`flex flex-row justify-between items-center p-3 w-[100%] bg-[#232425] rounded-3xl cursor-pointer hover:bg-[#2a2b2c] transition-colors ${
        isLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
      }`}
      style={{
        height: responsiveValueAdapter(74, true),
      }}
      onClick={onClick}
    >
      <div className="flex flex-row items-center gap-3">
        <div className="relative w-[50px] h-[50px]">
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={method.img_url}
              alt={method.name}
              style={{ width: '50px', height: '50px' }}
            />
          </div>
        </div>

        <div className="flex flex-col">
          <span className="font-semibold text-lg leading-[22px] tracking-[0.0128571em] text-white">
            {method.name}
          </span>
        </div>
      </div>
      <div className="w-6 h-6 flex items-center justify-center">
        <AppIcons name="chevronRight" size={24} color="icon.iconCard" />
      </div>
    </div>
  )
}

export default MessagingMethodsBottomSheet
